/* eslint-disable react-hooks/exhaustive-deps */
import {
  alpha,
  Button,
  Chip,
  Collapse,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Inventory2,
  Flatware,
  Visibility,
  PersonOutline,
  PhoneOutlined,
  CurrencyRupeeOutlined,
  EmailOutlined,
  EventNoteOutlined,
  SupportAgentOutlined,
  SourceOutlined,
  FlagOutlined,
  ErrorOutline,
  PeopleRounded,
  Add as AddIcon,
} from "@mui/icons-material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Box, Stack } from "@mui/system";
import { useFormik } from "formik";
import produce from "immer";
import moment from "moment";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useDispatchWrapper } from "../../../../hooks";
import { api } from "../../../../api/api";
import { IStoreState } from "../../../../redux/initialStoreState";
import {
  ILead,
  IMultipleEvent,
  initialRoughEstimate,
  IRoughEstimate,
  IRoughEstimateEvent,
  IRoughEstimateUpsertPayload,
  IStayDetail,
  ItemCharge,
} from "../../../../redux/leads/leads.types";
import {
  closeLoaderWithMessage,
  openLoaderWithMessage,
  showMessage,
} from "../../../../redux/messages/messagesActions";
import {
  fetchRoughEstimateAsync,
  fetchRoughEstimateByMultipleEventsAsync,
  upsertRoughEstimateAsync,
} from "../../../../redux/leads/leadsActions";
import { fetchRoomListAsync } from "../../../../redux/room/roomActions";
import { PageLoader } from "../../../../components/PageLoader/PageLoader";
import { Dialog } from "../../../../components/Dialogs/Dialog";
import { ALLOWED_MODULES, LoadState } from "../../../../constants/enums";
import { PageContainer } from "../../../../components/container/PageContainer";
import {
  CustomFormLabel,
  CustomTextField,
} from "../../../../components/formsComponents";
import { RoleBasedCustomButton } from "../../../../security/RoleBasedComponents/RoleBasedCustomButton/RoleBasedCustomButton";
import { IndianPriceFormatter } from "../../../../helpers/formatWithCommas";
import { CustomChip } from "../../../../components/CustomChip";
import { DetailInfoCard } from "../../../../components/DetailInfoCard/DetailInfoCard";
import { RoughEstimatePreviewV2 } from "./dialogs/RoughEstimatePreviewV2";
import { useBillingCompanyContext } from "../../../../providers";
import { ButtonWithRemark } from "../../../../components/ButtonWithRemark";
import { PaymentDetailsDialog } from "../PaymentDetails/PaymentDetailsDialog";
import { RoughEstimateHistory } from "./RoughEstimateHistory";
import { IFileUpload } from "../../../../components/FileUpload/FileUpload.type";
import { FileUpload } from "../../../../components/FileUpload/components/FileUpload";
import { initiateWorkFlow } from "../../../../redux/common/commonActions";
import { endPoints } from "../../../../constants/endPoints";
import { IServiceItem } from "../../../../redux/service";
import { IEventLeadsOptions } from "../../ManageLead";
import { LeadStatusRenderer } from "../../../../components/LeadStatusRenderer/LeadStatusRenderer";
import { ServicesAutoSearch } from "../../../../components/AutoCompleteSearches";
import { ViewToggle } from "../../../../components/ViewToggle";
import { useDataTableV2Context } from "../../../../components/TableV2/context/DataTableV2Provider";
import { VIEWS_CACHING_KEYS } from "../../../../constants/constants";
import { MasterFoodMenuAutocomplete } from "../../../../components/AutoCompleteSearches/MasterFoodMenuAutocomplete";
import { IMasterFoodMenu } from "../../../../redux/food/food.types";
import { EventCard, IEventCardData } from "../../../../components/EventCard/EventCard";
import { useTimezoneFormatter } from "../../../../hooks/useTimezoneFormatter";
import { isNotNull } from "../../../../helpers/isNotNull";
import {
  sendGenericWhatsAppTemplateAsync,
} from "../../../../components/ShareViaWhatsApp";
import { IWAServiceProvider } from "../../../../components/ShareViaWhatsApp/ShareViaWhatsApp.types";
import { ShareV2Button } from "./components/ActionButtons";
import { config } from "../../../../config";
import { TaxType } from "./components/TaxTypeSelector";
import {
  QuotationStayDetails,
  StayRowRequiredField,
} from "./components/QuotationStayDetails";
import { QuotationItemsEditor } from "./components/QuotationItemsEditor";
import { QuotationSummary } from "./components/QuotationSummary";
import { CreateEventLeadsDialog } from "./dialogs/CreateEventLeadsDialog";

// Room types are fetched dynamically from redux store via get-rooms endpoint

/** A single quotation now covers every event of the lead, so preview/PDF always render them together. */
const MULTIPLE_EVENTS_PREVIEW = 1;

const createEmptyStayRow = (): IStayDetail => ({
  room_name: "",
  check_in: null,
  check_out: null,
  rooms: 1,
  nights: 0,
  price: 0,
  sub_total: 0,
  discount_mode: "percentage",
  discount: "",
  gst_percentage: 0,
  gst_amount: 0,
  total: 0,
});

function calcNights(checkIn: string | null, checkOut: string | null): number {
  if (!checkIn || !checkOut) return 0;
  const diff = moment(checkOut).diff(moment(checkIn), "days");
  return Math.max(0, diff);
}

function getStayGross(row: IStayDetail) {
  return (
    Number(row.price || 0) * Number(row.rooms || 0) * Number(row.nights || 0)
  );
}

/** Stay amount after the row's discount (GST is applied on this value). */
function getStayTaxableBase(row: IStayDetail) {
  const gross = getStayGross(row);
  const d = Number(row.discount || 0);
  const discountAmount =
    row.discount_mode === "percentage"
      ? (gross * Math.min(Math.max(d, 0), 100)) / 100
      : Math.min(Math.max(d, 0), gross);
  return Math.max(0, gross - discountAmount);
}

function recalcStayRow(row: IStayDetail): IStayDetail {
  const withNights = { ...row, nights: calcNights(row.check_in, row.check_out) };
  const taxable = getStayTaxableBase(withNights);
  const gstAmount = (taxable * Number(row.gst_percentage || 0)) / 100;
  return {
    ...withNights,
    sub_total: getStayGross(withNights), // price × rooms × nights
    gst_amount: gstAmount,
    total: taxable + gstAmount,
  };
}

function mapStayRowForApi(
  row: IStayDetail,
): IRoughEstimateUpsertPayload["stay_details"][number] {
  return {
    room_name: row.room_name,
    check_in: row.check_in,
    check_out: row.check_out,
    rooms: Number(row.rooms || 0),
    nights: Number(row.nights || 0),
    amount: Number(row.price || 0),
    discount: Number(row.discount || 0),
    discount_mode: row.discount_mode === "amount" ? "amount" : "percentage",
    gst_amount: Number(row.gst_amount || 0),
    vat_amount: Number(row.vat_amount || 0),
    total: Number(row.total || 0),
  };
}

type ItemLineDiscountMode = "percentage" | "amount";

function normalizeItemLineDiscountMode(
  mode: string | undefined | null,
): ItemLineDiscountMode {
  return mode === "amount" ? "amount" : "percentage";
}

function getItemLineGrossSubTotal(
  amount: string | number | undefined,
  count: string | number | undefined,
) {
  return isNotNull(amount) * isNotNull(count);
}

/** Taxable amount after per-line discount; GST is applied on this value. */
function getItemDiscountedTaxableBase(
  grossSubTotal: number,
  discountMode: string | undefined | null,
  discount: string | number | undefined | null,
) {
  const mode = normalizeItemLineDiscountMode(discountMode);
  const d = Number(discount || 0);
  let discountAmount = 0;
  if (mode === "percentage") {
    const pct = Math.min(Math.max(d, 0), 100);
    discountAmount = (grossSubTotal * pct) / 100;
  } else {
    discountAmount = Math.min(Math.max(d, 0), grossSubTotal);
  }
  return Math.max(0, grossSubTotal - discountAmount);
}

function recalcItemChargeFields(charge: ItemCharge): ItemCharge {
  const discount_mode = normalizeItemLineDiscountMode(
    charge.discount_mode ?? "percentage",
  );
  const discount =
    charge.discount !== undefined && charge.discount !== null
      ? String(charge.discount)
      : "";

  const gross = getItemLineGrossSubTotal(charge.amount, charge.count);
  const taxable = getItemDiscountedTaxableBase(
    gross,
    discount_mode,
    discount,
  );

  const taxType = charge.tax_type ?? "GST";
  const isVat = taxType === "VAT";

  // GST applies for GST/HST rows; VAT applies only for VAT rows.
  const gstPct = isVat ? 0 : isNotNull(charge.gst_percentage ?? 0);
  const gstAmount = (taxable * gstPct) / 100;

  const vatPct = isVat ? isNotNull(charge.vat_percentage ?? 0) : 0;
  const vatAmount = (taxable * vatPct) / 100;

  return {
    ...charge,
    tax_type: taxType,
    discount_mode,
    discount,
    sub_total: gross.toString(),
    gst_amount: gstAmount.toString(),
    vat_amount: vatAmount.toString(),
    total: taxable + gstAmount + vatAmount,
  };
}

function mapItemsChargesForApi(items: ItemCharge[] | undefined) {
  return (items || []).map((item) => {
    return {
      ...item,
      discount_mode: normalizeItemLineDiscountMode(item.discount_mode),
      discount: Number(item.discount || 0),
      attachments: item.attachments || [],
    };
  });
}

function getAttachmentKey(att: any): string {
  if (!att) return "";
  if (typeof att === "string") return att;
  return att.path || att.key || att.url || att.name || "";
}

function normalizeAtts(atts: any[] | null | undefined): IFileUpload[] {
  if (!Array.isArray(atts)) return [];
  return atts.map((item) =>
    typeof item === "string"
      ? { file: null, key: item, path: item }
      : item
  );
}

/** Merge the manually uploaded documents with every selected service item's attachments (deduped by path). */
function getMergedAttachments(
  manualAttachments: (IFileUpload | string)[] | null | undefined,
  itemsCharges: ItemCharge[] | undefined,
): IFileUpload[] {
  const allRaw = [
    ...(manualAttachments || []),
    ...(itemsCharges || []).flatMap((item) => item.attachments || []),
  ];

  const seen = new Set<string>();
  const merged: IFileUpload[] = [];
  for (const raw of allRaw) {
    if (!raw) continue;
    const dedupeKey = getAttachmentKey(raw);
    if (!dedupeKey || seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);
    if (typeof raw === "string") {
      merged.push({ file: null, key: dedupeKey, path: raw });
    } else {
      merged.push(raw);
    }
  }
  return merged;
}

/** Recalculate saved item rows and back-fill attachments from the matching service item. */
function enrichItemCharges(
  items: ItemCharge[] | undefined,
  availableServices: IServiceItem[],
): ItemCharge[] {
  return (items || []).map((c) => {
    let atts = c.attachments;
    if (!atts || atts.length === 0) {
      const matched = availableServices.find(
        (s) => s.service_item_name === c.name,
      );
      atts = matched?.attachments ? normalizeAtts(matched.attachments) : [];
    } else {
      atts = normalizeAtts(atts);
    }
    return recalcItemChargeFields({
      ...c,
      description: c.description ?? "",
      attachments: atts,
    } as ItemCharge);
  });
}

/** Events whose lead is DEAD or CANCELLED are left out of the quotation. */
const INACTIVE_LEAD_STATUSES = ["DEAD", "CANCELLED"];

function isInactiveLeadStatus(status: string | null | undefined) {
  return INACTIVE_LEAD_STATUSES.includes(String(status || "").toUpperCase());
}

function getStayRowMissingFields(row: IStayDetail): StayRowRequiredField[] {
  const missing: StayRowRequiredField[] = [];
  if (!row.room_name) missing.push("room_name");
  if (!row.check_in) missing.push("check_in");
  // Same-day check-out yields 0 nights, which isn't a valid stay
  if (!row.check_out || Number(row.nights || 0) <= 0) missing.push("check_out");
  if (!(Number(row.rooms) > 0)) missing.push("rooms");
  if (!(Number(row.price) > 0)) missing.push("price");
  return missing;
}

function buildEventFromLead(
  leadUuid: string | null,
  lead: Partial<ILead> | undefined,
): IRoughEstimateEvent {
  return {
    leads_uuid: leadUuid,
    booking_date: lead?.booking_date || null,
    function_date: lead?.function_date || null,
    venue_name: lead?.preferred_venue || null,
    guest_count: Number(lead?.number_of_guests || 0),
    items_charges: [],
  };
}

/**
 * The quotation as saved on the server. The grouped (multi-event) API returns
 * `rough_estimate_uuid` / `quotation_status` on each event rather than on the
 * quotation, so they are lifted from the default event (or the first saved one).
 */
function getSavedQuotation(
  estimates: IRoughEstimate[] | undefined,
): IRoughEstimate | null {
  const list = estimates || [];
  const grouped = list.find((e) => Array.isArray(e.events));
  if (!grouped) return list.find((e) => e.rough_estimate_uuid) || null;

  const events = grouped.events || [];
  const primary =
    events.find((e) => e.is_default_event === 1 && e.rough_estimate_uuid) ||
    events.find((e) => e.rough_estimate_uuid);
  const status = grouped.status || primary?.quotation_status;
  return {
    ...grouped,
    rough_estimate_uuid:
      grouped.rough_estimate_uuid || primary?.rough_estimate_uuid || null,
    ...(status ? { status } : {}),
  };
}

/**
 * Build the editable quotation from the fetched data. The API returns one
 * quotation per `multiple_events_uuid` with per-event `items_charges`; older
 * per-lead estimates (no `events`) are folded into the same shape. Every event
 * of the lead gets a row, even when it has no saved items yet.
 */
function normalizeQuotation(
  estimates: IRoughEstimate[] | undefined,
  eventLeadsOptions: IEventLeadsOptions[] | undefined,
  fallbackLead: ILead | undefined,
  fallbackLeadUuid: string,
  availableServices: IServiceItem[],
): IRoughEstimate {
  const list = estimates || [];
  const saved = getSavedQuotation(list);
  let base: IRoughEstimate;
  let savedEvents: IRoughEstimateEvent[];
  if (saved && Array.isArray(saved.events)) {
    base = saved;
    savedEvents = saved.events;
  } else {
    base = saved || list[0] || initialRoughEstimate;
    savedEvents = list.map((e) => ({
      leads_uuid: e.leads_uuid,
      booking_date: e.booking_date,
      function_date: e.function_date,
      venue_name: e.venue_name,
      guest_count: e.guest_count,
      items_charges: e.items_charges || [],
    }));
  }

  const savedByLead = new Map(savedEvents.map((e) => [e.leads_uuid, e]));
  const options = eventLeadsOptions || [];
  // Drop DEAD/CANCELLED events, including their saved rows so they don't resurface below.
  options
    .filter((option) => isInactiveLeadStatus(option.leadStatus))
    .forEach((option) => savedByLead.delete(option.leadUuid));
  savedByLead.forEach((saved, leadUuid) => {
    if (isInactiveLeadStatus(saved.lead_status || saved.actual_status)) {
      savedByLead.delete(leadUuid);
    }
  });

  const events: IRoughEstimateEvent[] = options
    .filter((option) => !isInactiveLeadStatus(option.leadStatus))
    .map((option) => {
      const defaults = buildEventFromLead(option.leadUuid, option.leadData);
      const saved = savedByLead.get(option.leadUuid);
      savedByLead.delete(option.leadUuid);
      if (!saved) return defaults;
      return {
        ...saved,
        booking_date: saved.booking_date || defaults.booking_date,
        function_date: saved.function_date || defaults.function_date,
        venue_name: saved.venue_name || defaults.venue_name,
        guest_count: Number(saved.guest_count || defaults.guest_count || 0),
      };
    });
  // Saved events that aren't in the options (e.g. invoice view) are kept as-is.
  savedByLead.forEach((saved) => events.push(saved));
  // Fall back to the current lead only when no event list is available at all.
  if (
    events.length === 0 &&
    options.length === 0 &&
    fallbackLeadUuid &&
    !isInactiveLeadStatus(fallbackLead?.actual_status)
  ) {
    events.push(buildEventFromLead(fallbackLeadUuid, fallbackLead));
  }

  return {
    ...initialRoughEstimate,
    ...base,
    items_charges: [],
    events: events.map((e) => ({
      ...e,
      items_charges: enrichItemCharges(e.items_charges, availableServices),
    })),
  };
}

function normalizeStayDetails(rows: IStayDetail[] | undefined): IStayDetail[] {
  return (rows || []).map((row) => {
    const merged: IStayDetail = {
      ...createEmptyStayRow(),
      ...row,
      rooms: Number(row.rooms ?? 1),
      price: row.price ?? row.amount ?? 0,
      discount: row.discount ?? "",
      discount_mode: row.discount_mode === "amount" ? "amount" : "percentage",
    };
    // The API stores only the GST amount; back out the rate so recalculation keeps it.
    if (!Number(row.gst_percentage) && Number(row.gst_amount) > 0) {
      const taxable = getStayTaxableBase({
        ...merged,
        nights: calcNights(merged.check_in, merged.check_out),
      });
      if (taxable > 0) {
        merged.gst_percentage =
          Math.round((Number(row.gst_amount) / taxable) * 10000) / 100;
      }
    }
    return recalcStayRow(merged);
  });
}

interface IRoughEstimateProps {
  leadUUID: string;
  initialLeadToRoughEstimate: ILead;
  showActivityRoot?: boolean;
  isInvoiceView?: boolean;
  onAddAdvanceAmount?: () => void;
  onSave?: () => void;
  onCancellation?: () => void;
  hideActionButtons?: boolean;
  eventLeadsOptions?: IEventLeadsOptions[];
  skipInitialFetch?: boolean;
  /**
   * Enables the "+" on each event row, which creates a new lead from that event.
   * Called after the upsert so the parent can reload the lead's events (the rows
   * of this table come from `eventLeadsOptions`).
   */
  onEventLeadsCreated?: (createdLeads: ILead[]) => void;
}
export const ManageRoughEstimate: React.FC<IRoughEstimateProps> = (props) => {
  const {
    leadUUID,
    initialLeadToRoughEstimate,
    isInvoiceView,
    onAddAdvanceAmount,
    onSave,
    onCancellation,
    hideActionButtons = false,
    eventLeadsOptions,
    skipInitialFetch = false,
    onEventLeadsCreated,
  } = props;

  const isAdvance = true;
  const isCancellation = true;

  const isLeadInactive =
    initialLeadToRoughEstimate?.actual_status === "DEAD" ||
    initialLeadToRoughEstimate?.actual_status === "CANCELLED";

  // The quotation is shared by every event of the lead and keyed by multiple_events_uuid.
  const multipleEventsUuid =
    initialLeadToRoughEstimate?.multiple_events_uuid || null;
  /** Preview/PDF/share endpoints are keyed by the group uuid (lead uuid for legacy leads). */
  const quotationDocUuid = multipleEventsUuid || leadUUID;

  const dispatch = useDispatchWrapper();
  const navigate = useNavigate();
  const { ftDate } = useTimezoneFormatter();
  const companyOutlet = useBillingCompanyContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { saveDataInCache, cachingDataState, getCachedDataByKey } =
    useDataTableV2Context();

  const { data: requestedRoughEstimateData, loading: leadLoading } =
    useSelector((storeState: IStoreState) => storeState.leads.roughEstimate);

  const rooms = useSelector((storeState: IStoreState) => storeState.room.list.data);

  const [openAdvancePayment, setOpenAdvancePayment] =
    React.useState<boolean>(false);
  const [openCancellation, setOpenCancellation] =
    React.useState<boolean>(false);
  const [loading, setLoading] = React.useState(false);
  const [expandedEventIndex, setExpandedEventIndex] = React.useState<
    number | null
  >(null);
  const [openPreview, setOpenPreview] = React.useState<string | null>(null);
  /* Validation error shown in a dialog (e.g. trying to save with no items). */
  const [errorDialog, setErrorDialog] = React.useState<string | null>(null);
  /* Events the "create lead" dialog is prefilled from (null = closed). */
  const [createLeadSourceEvents, setCreateLeadSourceEvents] = React.useState<
    IMultipleEvent[] | null
  >(null);

  const [selectedService, setSelectedService] =
    React.useState<IServiceItem | null>(null);
  const [selectedMasterMenus, setSelectedMasterMenus] =
    React.useState<IMasterFoodMenu | null>(null);

  const [showPackage, setShowPackage] = useState(true);

  const [stayDetails, setStayDetails] = React.useState<IStayDetail[]>([]);
  /* Highlight empty stay fields once a save has been attempted. */
  const [showStayErrors, setShowStayErrors] = React.useState(false);

  const stayDetailsTotal = React.useMemo(
    () => stayDetails.reduce((acc, row) => acc + row.total, 0),
    [stayDetails],
  );

  const stayDetailsGstTotal = React.useMemo(
    () => stayDetails.reduce((acc, row) => acc + row.gst_amount, 0),
    [stayDetails],
  );

  const [availableServices, setAvailableServices] = React.useState<IServiceItem[]>([]);

  React.useEffect(() => {
    if (companyOutlet?.comapny?.company_outlet_uuid) {
      const fetchServices = async () => {
        try {
          const res = await api.get(
            `/service/get-service-item?company_outlet_uuid=${companyOutlet.comapny.company_outlet_uuid}`
          );
          if (res?.data?.data) {
            setAvailableServices(res.data.data);
          }
        } catch (err) {
          console.error("Error fetching services for rough estimate:", err);
        }
      };
      fetchServices();
    }
  }, [companyOutlet?.comapny?.company_outlet_uuid]);

  // The quotation as last saved on the server (drives statuses shown in the UI).
  const savedQuotation = React.useMemo(
    () => getSavedQuotation(requestedRoughEstimateData),
    [requestedRoughEstimateData],
  );

  const eventOptionsByLead = React.useMemo(
    () => new Map((eventLeadsOptions || []).map((o) => [o.leadUuid, o])),
    [eventLeadsOptions],
  );

  // Stable key so parent re-renders with a new (but equal) options array don't reset the form.
  // Includes the lead status so an event turning DEAD/CANCELLED drops out of the list.
  const eventOptionsKey = (eventLeadsOptions || [])
    .map((o) => `${o.leadUuid}:${o.leadStatus}`)
    .join(",");

  // Parents like ManageLead mount this before their lead has loaded, so a missing
  // multiple_events_uuid only means "legacy lead" once the lead itself is present.
  const isLeadLoaded = Boolean(initialLeadToRoughEstimate?.leads_uuid);

  const fetchQuotation = () => {
    if (multipleEventsUuid) {
      dispatch(fetchRoughEstimateByMultipleEventsAsync(multipleEventsUuid));
    } else if (leadUUID && isLeadLoaded) {
      // Lead without a group uuid (legacy records) — fall back to the per-lead fetch.
      dispatch(fetchRoughEstimateAsync(leadUUID, isInvoiceView ? null : 0));
    }
  };

  const saveQuotation = (onSuccess?: () => void) => {
    setLoading(true);
    dispatch(openLoaderWithMessage("Saving, Please wait...."));
    dispatch(
      upsertRoughEstimateAsync(buildPayload(), (isSuccess, data) => {
        dispatch(closeLoaderWithMessage());
        setLoading(false);
        if (isSuccess && data) {
          fetchQuotation();
          onSuccess?.();
        }
      }),
    );
  };

  /** Highlights empty required stay fields and returns false when any row is incomplete. */
  const validateStayDetails = () => {
    const isValid = stayDetails.every(
      (row) => getStayRowMissingFields(row).length === 0,
    );
    setShowStayErrors(!isValid);
    return isValid;
  };

  const { values, handleSubmit, setValues, setFieldValue } = useFormik({
    initialValues: initialRoughEstimate,
    onSubmit: async (values) => {
      if (!canSave) {
        setErrorDialog(
          "Add at least one item or enter the required details before saving.",
        );
        return;
      }
      if (!validateStayDetails()) return;
      saveQuotation(onSave);

      dispatch(
        initiateWorkFlow(
          initialLeadToRoughEstimate,
          { ...values, status: "CANCELLED" },
          endPoints.leads.upsertLead,
          true,
          "lead_id",
          leadUUID,
        ),
      );
    },
  });

  const quotationEvents = values.events || [];

  const allItems = React.useMemo(
    () => quotationEvents.flatMap((e) => e.items_charges || []),
    [quotationEvents],
  );

  const hasSavedQuotation = !!values.rough_estimate_uuid;

  // Guard: can save only if at least one item or stay row exists
  const canSave = allItems.length > 0 || stayDetails.length > 0;

  // ─── Derived Financial Calculations ───────────────────────────────────────
  // Totals span the shared stay details and the items of every event.
  const financialSummary = React.useMemo(() => {
    const Discount = Number(values.discount || 0);
    const Extras = Number(values.extras_amount || 0);

    const grossLineTotal = allItems.reduce(
      (acc, curr) => acc + getItemLineGrossSubTotal(curr.amount, curr.count),
      0,
    );

    const netAfterLineDiscounts = allItems.reduce(
      (acc, curr) =>
        acc +
        getItemDiscountedTaxableBase(
          getItemLineGrossSubTotal(curr.amount, curr.count),
          curr.discount_mode,
          curr.discount,
        ),
      0,
    );

    const stayGrossTotal = stayDetails.reduce(
      (acc, row) => acc + getStayGross(row),
      0,
    );
    const stayTaxableTotal = stayDetails.reduce(
      (acc, row) => acc + getStayTaxableBase(row),
      0,
    );

    const itemLineDiscount = Math.max(0, grossLineTotal - netAfterLineDiscounts);
    const stayLineDiscount = Math.max(0, stayGrossTotal - stayTaxableTotal);
    const lineDiscountTotal = itemLineDiscount + stayLineDiscount;

    // Bill-level discount, computed over the taxable base.
    const totalBeforeDiscount = netAfterLineDiscounts + Extras + stayTaxableTotal;
    let discountAmount = 0;
    if (values.discount_mode === "percentage") {
      const pct = Math.min(Math.max(Discount, 0), 100);
      discountAmount = (totalBeforeDiscount * pct) / 100;
    } else {
      discountAmount = Math.min(Math.max(Discount, 0), totalBeforeDiscount);
    }

    const amountAfterDisc = Math.max(0, totalBeforeDiscount - discountAmount);
    const subTotal = grossLineTotal + stayGrossTotal;

    // Tax
    const taxPercentage = Number(values.tax_percentage || 0);
    const taxAmount = taxPercentage > 0 ? amountAfterDisc * (taxPercentage / 100) : 0;

    // GST from items + stay
    const itemsGst = allItems.reduce(
      (acc, curr) => acc + Number(curr.gst_amount || 0),
      0,
    );
    const totalGst = itemsGst + stayDetailsGstTotal;

    // VAT from items (stay rows are GST-only)
    const totalVat = allItems.reduce(
      (acc, curr) => acc + Number(curr.vat_amount || 0),
      0,
    );

    // Grand total (includes both GST and VAT)
    const grandTotal = amountAfterDisc + taxAmount + totalGst + totalVat;

    // Remaining
    const totalPaid = Number(values.total_amount_paid || 0);
    const remainingBalance = grandTotal - totalPaid;

    return {
      subTotal,
      lineDiscountTotal,
      overallDiscountAmount: discountAmount,
      taxAmount,
      totalGst,
      totalVat,
      grandTotal,
      remainingBalance,
    };
  }, [
    allItems,
    values.discount,
    values.discount_mode,
    values.extras_amount,
    values.tax_percentage,
    values.total_amount_paid,
    stayDetails,
    stayDetailsGstTotal,
  ]);

  // Single effect to sync computed values into formik
  React.useEffect(() => {
    setFieldValue("sub_total", financialSummary.subTotal);
    setFieldValue("tax_amount", financialSummary.taxAmount.toFixed(2));
    setFieldValue("total_gst", financialSummary.totalGst);
    setFieldValue("total_vat", financialSummary.totalVat);
    setFieldValue("grand_total", financialSummary.grandTotal);
    setFieldValue("remaining_balance", financialSummary.remainingBalance);
  }, [financialSummary, setFieldValue]);

  function buildPayload(): IRoughEstimateUpsertPayload {
    return {
      rough_estimate_uuid: values.rough_estimate_uuid || null,
      multiple_events_uuid: multipleEventsUuid,
      company_outlet_uuid: companyOutlet.comapny.company_outlet_uuid,
      contact_number:
        values.contact_number ||
        initialLeadToRoughEstimate?.contact_number_one ||
        null,
      gst_pan_number: values.gst_pan_number || null,
      status: values.status,
      attachments: getMergedAttachments(values.attachments, allItems),
      remarks: values.remarks || "",
      stay_details: stayDetails.map(mapStayRowForApi),
      discount_mode: values.discount_mode,
      discount: String(values.discount ?? "0"),
      sub_total: financialSummary.subTotal,
      total_gst: financialSummary.totalGst,
      total_vat: financialSummary.totalVat,
      tax_amount: financialSummary.taxAmount.toFixed(2),
      tax_percentage: values.tax_percentage ?? null,
      extras_amount: String(values.extras_amount ?? "0.00"),
      advance_amount: values.advance_amount ?? null,
      grand_total: Math.round(financialSummary.grandTotal || 0).toString(),
      events: quotationEvents.map((event) => ({
        leads_uuid: event.leads_uuid,
        booking_date: event.booking_date || null,
        function_date: event.function_date || null,
        venue_name: event.venue_name || null,
        guest_count: Number(event.guest_count || 0),
        items_charges: mapItemsChargesForApi(event.items_charges),
      })),
    };
  }

  /** Index of the expanded event — new items/packages are added to it. */
  const activeEventIndex = expandedEventIndex ?? 0;

  const updateEventItems = (
    eventIndex: number,
    recipe: (items: ItemCharge[]) => void,
  ) => {
    setFieldValue(
      "events",
      produce(quotationEvents, (draft) => {
        if (!draft[eventIndex]) return;
        recipe(draft[eventIndex].items_charges as ItemCharge[]);
      }),
    );
  };

  const addAttachmentsFromService = (attachments: IFileUpload[] | null | undefined) => {
    if (!attachments || attachments.length === 0) return;
    setFieldValue(
      "attachments",
      getMergedAttachments(values.attachments, [{ attachments } as ItemCharge]),
    );
  };

  const handleAddCharge = () => {
    updateEventItems(activeEventIndex, (items) => {
      items.push(
        recalcItemChargeFields({
          name: "",
          description: "",
          amount: "",
          count: 1,
          gst_percentage: 0,
          gst_amount: "0",
          tax_type: "GST",
          vat_percentage: 0,
          vat_amount: "0",
          sub_total: "0",
          total: 0,
          discount_mode: "percentage",
          discount: "",
        }),
      );
    });
  };

  const handleSaveAndPreview = () => {
    if (!canSave) {
      setErrorDialog(
        "Please add at least one item or complete the stay details before saving the quotation.",
      );
      return;
    }
    if (!validateStayDetails()) return;
    saveQuotation(() => setOpenPreview(quotationDocUuid));
  };

  const handleAddAdvanceAmount = () => {
    if (onAddAdvanceAmount) {
      onAddAdvanceAmount();
      return;
    }

    if (Number(values.grand_total) > 0 && !values.rough_estimate_uuid) {
      handleSubmit();
      setOpenAdvancePayment(true);
    } else if (values.rough_estimate_uuid) {
      setOpenAdvancePayment(true);
    } else {
      dispatch(
        showMessage({
          type: "error",
          message: "First Fill Rough Estimation Before Adding Advance Amount",
          displayAs: "snackbar",
        }),
      );
    }
  };

  const handleSendWhatsapp = async (provider: IWAServiceProvider) => {
    if (!values.rough_estimate_uuid) return;
    try {
      dispatch(openLoaderWithMessage("Sending WhatsApp message..."));
      const rawPhone = (values.contact_number || initialLeadToRoughEstimate?.contact_number_one || "").replace(/\D/g, "");
      const countryCode = (initialLeadToRoughEstimate?.country_code || "91").replace(/\D/g, "") || "91";
      const toNumber = rawPhone.startsWith(countryCode) ? rawPhone : `${countryCode}${rawPhone}`;
      const pdfUrl = `${config.api.baseUrl}/leads/rough-estimate/${quotationDocUuid}/PDF?multiple_events=${MULTIPLE_EVENTS_PREVIEW}`;

      await sendGenericWhatsAppTemplateAsync({
        company_outlet_uuid: companyOutlet.comapny.company_outlet_uuid,
        platform: provider || "META",
        env_key: "GENERIC_QUOTATION_WA",
        toNumber,
        payload: {
          name_of_guest: initialLeadToRoughEstimate?.name_of_guest || "",
          rough_estimation_pdf: pdfUrl,
        },
      });

      dispatch(
        showMessage({
          type: "success",
          message: "Quotation sent successfully via WhatsApp",
          displayAs: "snackbar",
        }),
      );
    } catch (error: any) {
      dispatch(
        showMessage({
          type: "error",
          message:
            error?.response?.data?.message ||
            "Failed to send quotation via WhatsApp",
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(closeLoaderWithMessage());
    }
  };

  const handleShowCard = (value: boolean) => () => {
    saveDataInCache(VIEWS_CACHING_KEYS.ROUGH_ESTIMATE_VIEW_CACHE_KEY, {
      showPackage: value,
    });
    setShowPackage(value);
  };

  React.useEffect(() => {
    const data = getCachedDataByKey(
      VIEWS_CACHING_KEYS.ROUGH_ESTIMATE_VIEW_CACHE_KEY,
    );
    setShowPackage(data ? data.showPackage : true);
  }, [cachingDataState[VIEWS_CACHING_KEYS.ROUGH_ESTIMATE_VIEW_CACHE_KEY]]);

  const isInitialMount = React.useRef(true);

  React.useEffect(() => {
    // Wait for the lead so we know its multiple_events_uuid before choosing an endpoint
    if (!multipleEventsUuid && !(leadUUID && isLeadLoaded)) return;
    // Skip initial fetch if skipInitialFetch is true (data already pre-fetched by parent)
    if (isInitialMount.current && skipInitialFetch) {
      isInitialMount.current = false;
      return;
    }
    isInitialMount.current = false;
    fetchQuotation();
  }, [
    multipleEventsUuid,
    leadUUID,
    isLeadLoaded,
    dispatch,
    openCancellation,
    openAdvancePayment,
    skipInitialFetch,
    isInvoiceView,
  ]);

  React.useEffect(() => {
    if (companyOutlet?.comapny?.company_outlet_uuid) {
      dispatch(
        fetchRoomListAsync(
          companyOutlet.comapny.company_outlet_uuid,
          1,
          1000,
          1000,
          "ACTIVE"
        )
      );
    }
  }, [companyOutlet?.comapny?.company_outlet_uuid, dispatch]);

  const companyGstin = companyOutlet?.comapny?.company_outlet_gstin_number || "";

  // Load the fetched quotation into the form.
  React.useEffect(() => {
    const quotation = normalizeQuotation(
      requestedRoughEstimateData,
      eventLeadsOptions,
      initialLeadToRoughEstimate,
      leadUUID,
      availableServices,
    );
    setValues({
      ...quotation,
      leads_uuid: leadUUID || quotation.leads_uuid,
      multiple_events_uuid: multipleEventsUuid || quotation.multiple_events_uuid,
      // Default to the billing company's GSTIN (Company Info page) when none is saved
      gst_pan_number: quotation.gst_pan_number || companyGstin || null,
      // Only saved quotations carry stay details
      ...(quotation.rough_estimate_uuid ? {} : { stay_details: [] }),
    });
    setStayDetails(
      quotation.rough_estimate_uuid
        ? normalizeStayDetails(quotation.stay_details)
        : [],
    );
    setShowStayErrors(false);
  }, [requestedRoughEstimateData, availableServices, eventOptionsKey, companyGstin, setValues]);

  // Collapse the expanded event if the event list shrinks.
  React.useEffect(() => {
    if (
      expandedEventIndex !== null &&
      expandedEventIndex >= quotationEvents.length
    ) {
      setExpandedEventIndex(null);
    }
  }, [quotationEvents.length, expandedEventIndex]);

  // Handle immediate service addition when selected
  React.useEffect(() => {
    if (!selectedService) return;

    const serviceItemName = selectedService.service_item_name;
    updateEventItems(activeEventIndex, (items) => {
      const existing = items.find((charge) => charge.name === serviceItemName);
      if (existing) {
        // Service exists - increment count and update totals
        existing.count = (Number(existing.count || 0) + 1) as any;
        existing.attachments = selectedService.attachments || existing.attachments;
        Object.assign(existing, recalcItemChargeFields(existing as ItemCharge));
      } else {
        const price =
          selectedService.total_price ||
          selectedService.variant?.[0]?.price ||
          0;
        items.push(
          recalcItemChargeFields({
            name: selectedService.service_item_name,
            description: "",
            amount: price.toString(),
            count: selectedService.total_quantity || 1,
            sub_total: "0",
            gst_amount: "0",
            gst_percentage: 0,
            tax_type: "GST",
            vat_percentage: 0,
            vat_amount: "0",
            total: 0,
            discount_mode: "percentage",
            discount: "",
            attachments: selectedService.attachments || [],
          }),
        );
      }
    });
    addAttachmentsFromService(selectedService.attachments);

    setSelectedService(null);
  }, [selectedService]);

  // Handle immediate package addition when selected
  React.useEffect(() => {
    if (!selectedMasterMenus) return;

    const masterMenuName = selectedMasterMenus.title;
    updateEventItems(activeEventIndex, (items) => {
      const existing = items.find((charge) => charge.name === masterMenuName);
      if (existing) {
        existing.count = (Number(existing.count || 0) + 1) as any;
        Object.assign(existing, recalcItemChargeFields(existing as ItemCharge));
      } else {
        const price =
          selectedMasterMenus.price ||
          selectedMasterMenus.variant?.[0]?.price ||
          0;
        items.push(
          recalcItemChargeFields({
            name: selectedMasterMenus.title,
            description: selectedMasterMenus.description ?? "",
            amount: price.toString(),
            count: selectedMasterMenus.total_quantity || 1,
            sub_total: "0",
            gst_amount: "0",
            gst_percentage: 0,
            // Carry the package's tax type so the row shows GST or VAT accordingly
            tax_type: selectedMasterMenus.tax_type ?? "GST",
            vat_percentage: 0,
            vat_amount: "0",
            total: 0,
            discount_mode: "percentage",
            discount: "",
          }),
        );
      }
    });

    setSelectedMasterMenus(null);
  }, [selectedMasterMenus]);

  const handleItemsChange = (
    eventIndex: number,
    index: number,
    key: string,
    value: any,
  ) => {
    const recalcKeys = new Set([
      "amount",
      "count",
      "gst_percentage",
      "vat_percentage",
      "tax_type",
      "discount",
      "discount_mode",
    ]);
    updateEventItems(eventIndex, (items) => {
      if (!items[index]) return;
      (items[index] as any)[key] = value;
      if (recalcKeys.has(key)) {
        Object.assign(items[index], recalcItemChargeFields(items[index]));
      }
    });
  };

  /**
   * Switch a line item's tax type between GST and VAT. When switching we clear
   * the now-inactive tax's percentage + amount so stale values don't linger,
   * then recompute the row through the single source of truth.
   */
  const handleTaxTypeChange = (
    eventIndex: number,
    index: number,
    nextType: TaxType,
  ) => {
    const current =
      quotationEvents[eventIndex]?.items_charges?.[index]?.tax_type ?? "GST";
    if (current === nextType) return;

    updateEventItems(eventIndex, (items) => {
      const row = items[index] as any;
      row.tax_type = nextType;
      if (nextType === "VAT") {
        // Moving GST → VAT: clear GST fields.
        row.gst_percentage = 0;
        row.gst_amount = "0";
      } else {
        // Moving VAT → GST: clear VAT fields.
        row.vat_percentage = 0;
        row.vat_amount = "0";
      }
      Object.assign(items[index], recalcItemChargeFields(items[index]));
    });
  };

  const handleDeleteItemCharge = (eventIndex: number, indexToRemove: number) => {
    const itemToRemove = quotationEvents[eventIndex]?.items_charges?.[indexToRemove];
    const nextEvents = produce(quotationEvents, (draft) => {
      draft[eventIndex]?.items_charges.splice(indexToRemove, 1);
    });
    setFieldValue("events", nextEvents);

    const remainingItems = nextEvents.flatMap((e) => e.items_charges || []);

    // If no charges/packages remain in any event, reset attachments of the quotation
    if (remainingItems.length === 0) {
      setFieldValue("attachments", null);
      return;
    }

    // Determine which attachment keys belong to the deleted package
    let deletedAtts = itemToRemove?.attachments || [];
    if (deletedAtts.length === 0 && itemToRemove?.name) {
      const matched = availableServices.find(
        (s) => s.service_item_name === itemToRemove.name
      );
      if (matched && matched.attachments) {
        deletedAtts = normalizeAtts(matched.attachments);
      }
    }

    // Keep attachments still used by an item in another event
    const stillUsedKeys = new Set(
      remainingItems.flatMap((i) => (i.attachments || []).map(getAttachmentKey)),
    );
    const deletedKeys = new Set(
      deletedAtts
        .map(getAttachmentKey)
        .filter((key) => key && !stillUsedKeys.has(key)),
    );

    if (deletedKeys.size > 0) {
      const updatedAttachments = (values.attachments || []).filter((att) => {
        const key = getAttachmentKey(att);
        return !key || !deletedKeys.has(key);
      });

      setFieldValue(
        "attachments",
        updatedAttachments.length > 0 ? updatedAttachments : null,
      );
    }
  };

  const handleAddStayRow = () => {
    setStayDetails((prev) => [...prev, createEmptyStayRow()]);
  };

  const handleRemoveStayRow = (index: number) => {
    setStayDetails((prev) => prev.filter((_, i) => i !== index));
  };

  const handleStayChange = (index: number, key: keyof IStayDetail, value: any) => {
    if (key === "room_name" && value === "ADD_ROOM") {
      navigate("/room-types");
      return;
    }
    setStayDetails((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        const updated = { ...row, [key]: value };
        if (key === "room_name") {
          const selectedRoom = rooms.find(r => r.room_name === value);
          if (selectedRoom) {
            updated.price = Number(selectedRoom.room_price) || 0;
          }
        }
        return recalcStayRow(updated);
      }),
    );
  };

  const toggleEvent = (index: number) => {
    setExpandedEventIndex((prev) => (prev === index ? null : index));
  };

  const getEventTitle = (event: IRoughEstimateEvent) => {
    const option = event.leads_uuid ? eventOptionsByLead.get(event.leads_uuid) : undefined;
    if (option) {
      return option.eventType === "Other Social"
        ? option.eventName || "Other Social"
        : option.eventType || "--";
    }
    return event.event_name || event.event_type || "--";
  };

  const getEventLeadStatus = (event: IRoughEstimateEvent) =>
    (event.leads_uuid && eventOptionsByLead.get(event.leads_uuid)?.leadStatus) ||
    event.lead_status ||
    event.actual_status ||
    "";

  /** Full event lead behind a quotation row — the quotation event only has display fields. */
  const getSourceEventLead = (
    event: IRoughEstimateEvent,
  ): IMultipleEvent | undefined => {
    if (!event.leads_uuid) return undefined;
    const option = eventOptionsByLead.get(event.leads_uuid);
    if (option?.leadData) return option.leadData as unknown as IMultipleEvent;
    return initialLeadToRoughEstimate?.multiple_events_list?.find(
      (e) => e.leads_uuid === event.leads_uuid,
    );
  };

  const canCreateEventLead =
    Boolean(onEventLeadsCreated) && !isInvoiceView && !isLeadInactive;

  const renderCreateLeadButton = (event: IRoughEstimateEvent) => {
    const sourceEvent = canCreateEventLead ? getSourceEventLead(event) : undefined;
    if (!sourceEvent) return null;
    return (
      <Tooltip title="Create Lead from this Event">
        <IconButton
          color="primary"
          size="small"
          sx={{ p: 0.25 }}
          onClick={(e) => {
            e.stopPropagation();
            setCreateLeadSourceEvents([sourceEvent]);
          }}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  };

  const contactNumber =
    values.contact_number ||
    initialLeadToRoughEstimate?.contact_number_one ||
    "";

  const quotationStatus = savedQuotation?.status;
  const paymentStatus = savedQuotation?.payment_status || "UNPAID";
  const isQuotationSentLocked =
    loading || isLeadInactive || quotationStatus === "QUOTATION_SENT";

  const quotationGrandTotal =
    (hasSavedQuotation
      ? savedQuotation?.grand_total
      : initialLeadToRoughEstimate?.grand_total) ?? null;

  // ─── Render helpers ───────────────────────────────────────────────────────

  const renderAddItemToolbar = () => {
    if (isInvoiceView) return null;
    const search = showPackage ? (
      <ServicesAutoSearch
        label="Select Packages"
        multiple={false}
        value={selectedService}
        onSelect={(service) => {
          setSelectedService(service as IServiceItem | null);
        }}
      />
    ) : (
      <MasterFoodMenuAutocomplete
        value={selectedMasterMenus ? [selectedMasterMenus] : []}
        onChange={(masterMenus) => {
          setSelectedMasterMenus(masterMenus[0] as IMasterFoodMenu);
        }}
        companyOutletUuid={companyOutlet.comapny.company_outlet_uuid as string}
        searchOnType={false}
      />
    );
    const toggle = (
      <ViewToggle
        isGridView={showPackage}
        onToggle={handleShowCard(!showPackage)}
        size="medium"
        leftToggleIcon={<Inventory2 />}
        rightToggleIcon={<Flatware />}
      />
    );

    if (isMobile) {
      return (
        <Stack spacing={1.5} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ flex: 1, minWidth: 0 }}>{search}</Box>
            {toggle}
          </Stack>
          <Button
            variant="contained"
            size="medium"
            fullWidth
            disabled={isLeadInactive}
            onClick={handleAddCharge}
          >
            + Add Custom Charge
          </Button>
        </Stack>
      );
    }
    return (
      <Stack direction="row" spacing={2} alignItems="center">
        <Button
          variant="outlined"
          size="small"
          disabled={isLeadInactive}
          onClick={handleAddCharge}
        >
          + Add Custom Charge
        </Button>
        <Box sx={{ minWidth: 300 }}>{search}</Box>
        {toggle}
      </Stack>
    );
  };

  const renderEventItems = (eventIndex: number) => (
    <QuotationItemsEditor
      items={quotationEvents[eventIndex]?.items_charges || []}
      isMobile={isMobile}
      disabled={isLeadInactive}
      onItemChange={(index, key, value) =>
        handleItemsChange(eventIndex, index, key, value)
      }
      onTaxTypeChange={(index, next) =>
        handleTaxTypeChange(eventIndex, index, next)
      }
      onDelete={(index) => handleDeleteItemCharge(eventIndex, index)}
      footer={renderAddItemToolbar()}
    />
  );

  const renderEventsTable = () => (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{ mt: 1, width: "100%", borderRadius: 2, overflow: "hidden" }}
    >
      <Table
        size="small"
        sx={{
          // Compact every cell so the table packs into the available width.
          "& .MuiTableCell-root": { px: 0.75, py: 0.5 },
          "& .MuiChip-root": { height: 20, fontSize: "0.68rem" },
          "& .MuiChip-label": { px: 0.75 },
          // Last body row sits flush with the rounded bottom edge.
          "& .MuiTableBody-root .MuiTableRow-root:last-of-type .MuiTableCell-root":
            { borderBottom: "none" },
        }}
      >
        <TableHead>
          <TableRow
            sx={{
              backgroundColor: (t) => t.palette.primary.main,
              "& .MuiTableCell-root": {
                fontWeight: 700,
                fontSize: "0.75rem",
                letterSpacing: "0.02em",
                textTransform: "uppercase",
                color: "#fff",
                backgroundColor: "transparent",
                borderBottom: "none",
                py: 1,
                whiteSpace: "nowrap",
              },
            }}
          >
            <TableCell />
            <TableCell>Event Type</TableCell>
            <TableCell>Lead Status</TableCell>
            <TableCell>Quotation Status</TableCell>
            <TableCell>Payment Status</TableCell>
            <TableCell>GST/PAN Number</TableCell>
            <TableCell>Event Date</TableCell>
            <TableCell>Booking Date</TableCell>
            <TableCell>Contact</TableCell>
            <TableCell>Venue</TableCell>
            <TableCell># Guest</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {quotationEvents.map((event, index) => {
            const isExpanded = expandedEventIndex === index;
            return (
              <React.Fragment key={event.leads_uuid || index}>
                <TableRow
                  onClick={() => toggleEvent(index)}
                  sx={{
                    cursor: "pointer",
                    backgroundColor: isExpanded
                      ? (t) => alpha(t.palette.primary.main, 0.08)
                      : "inherit",
                    "&:hover": {
                      backgroundColor: (t) => alpha(t.palette.primary.main, 0.04),
                    },
                    "& .MuiTableCell-root": {
                      py: 1.2,
                      fontSize: "0.8125rem",
                      borderBottomColor: "divider",
                      whiteSpace: "nowrap",
                    },
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                      <IconButton
                        size="small"
                        sx={{ p: 0.25 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleEvent(index);
                        }}
                      >
                        {isExpanded ? (
                          <KeyboardArrowUpIcon fontSize="small" />
                        ) : (
                          <KeyboardArrowDownIcon fontSize="small" />
                        )}
                      </IconButton>
                      {renderCreateLeadButton(event)}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <CustomChip
                      color="primary"
                      content={getEventTitle(event)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <LeadStatusRenderer status={getEventLeadStatus(event)} />
                  </TableCell>
                  <TableCell>
                    {quotationStatus === "DRAFT" ||
                      quotationStatus === "QUOTATION_SENT" ? (
                      <CustomChip
                        color={quotationStatus === "QUOTATION_SENT" ? "success" : "warning"}
                        content={quotationStatus === "QUOTATION_SENT" ? "Sent" : "Draft"}
                        size="small"
                      />
                    ) : (
                      "--"
                    )}
                  </TableCell>
                  <TableCell>
                    <CustomChip
                      color={
                        paymentStatus === "PAID"
                          ? "success"
                          : paymentStatus === "PARTIALLY_PAID"
                            ? "warning"
                            : "error"
                      }
                      content={paymentStatus}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{values.gst_pan_number || "--"}</TableCell>
                  <TableCell>
                    {event.function_date ? ftDate(event.function_date) : "--"}
                  </TableCell>
                  <TableCell>
                    {event.booking_date ? ftDate(event.booking_date) : "--"}
                  </TableCell>
                  <TableCell>{contactNumber || "--"}</TableCell>
                  <TableCell>{event.venue_name || "--"}</TableCell>
                  <TableCell>{event.guest_count || 0}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ py: "0 !important" }} colSpan={11}>
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Box sx={{ my: 1 }}>{renderEventItems(index)}</Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderEventCards = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
      {quotationEvents.map((event, index) => {
        const cardData: IEventCardData = {
          title: getEventTitle(event),
          status: getEventLeadStatus(event) || quotationStatus || "",
          venue: event.venue_name || "--",
          eventDate: event.function_date || "",
          bookingDate: event.booking_date || "",
          guests: event.guest_count || 0,
          paymentStatus,
          gstPanNumber: values.gst_pan_number || "",
          contact: contactNumber,
        };
        return (
          <Box key={event.leads_uuid || index}>
            <EventCard
              data={cardData}
              index={index}
              expanded={expandedEventIndex === index}
              onClick={toggleEvent}
              renderActions={
                canCreateEventLead ? () => renderCreateLeadButton(event) : undefined
              }
            />
            <Collapse in={expandedEventIndex === index} timeout="auto" unmountOnExit>
              <Box sx={{ mt: 1, pl: 1, borderLeft: "2px solid", borderColor: "primary.light" }}>
                {renderEventItems(index)}
              </Box>
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );

  const actionButtonSx = isMobile
    ? {
      flexShrink: 0,
      whiteSpace: "nowrap",
      fontSize: "0.7rem",
      px: 1,
      py: 0.5,
      minWidth: "auto",
    }
    : {};

  const renderActions = () => {
    if (isInvoiceView) return null;
    return (
      <Stack
        direction="row"
        spacing={isMobile ? 0.5 : 2}
        marginTop={2}
        justifyContent="space-between"
        alignItems="center"
        pb={2}
        sx={{ flexWrap: isMobile ? "wrap" : "nowrap", rowGap: 1 }}
      >
        <Box sx={{ flexShrink: 0 }}>
          {hasSavedQuotation && (
            <Chip
              icon={
                values.status === "QUOTATION_SENT" ? (
                  <CheckCircleIcon sx={{ fontSize: isMobile ? 16 : 18 }} />
                ) : (
                  <DescriptionOutlinedIcon sx={{ fontSize: isMobile ? 16 : 18 }} />
                )
              }
              label={
                values.status === "QUOTATION_SENT"
                  ? "Quotation Sent"
                  : "Mark quotation sent"
              }
              size={isMobile ? "small" : "medium"}
              clickable={!isQuotationSentLocked}
              onClick={() => {
                if (isQuotationSentLocked) return;
                setFieldValue(
                  "status",
                  values.status === "QUOTATION_SENT"
                    ? quotationStatus
                    : "QUOTATION_SENT",
                );
              }}
              sx={{
                fontWeight: 600,
                fontSize: isMobile ? "0.75rem" : "0.85rem",
                height: isMobile ? undefined : 36,
                px: isMobile ? 1 : 1.5,
                ...(values.status === "QUOTATION_SENT"
                  ? {
                    backgroundColor: "#e8f5e9",
                    color: "#2e7d32",
                    border: "1px solid #a5d6a7",
                    "& .MuiChip-icon": { color: "#2e7d32" },
                  }
                  : {
                    backgroundColor: "#f5f5f5",
                    color: "#616161",
                    border: "1px solid #e0e0e0",
                    "& .MuiChip-icon": { color: "#9e9e9e" },
                  }),
                ...(isQuotationSentLocked
                  ? { opacity: 0.7, cursor: "default" }
                  : {}),
              }}
            />
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            flexWrap: isMobile ? "wrap" : "nowrap",
            gap: isMobile ? 0.5 : 2,
          }}
        >
          {hasSavedQuotation && (
            <RoughEstimateHistory
              roughEstimateUuid={values.rough_estimate_uuid as string}
              buttonSx={isMobile ? actionButtonSx : undefined}
            />
          )}
          {hasSavedQuotation && (
            <Button
              disabled={loading || isLeadInactive}
              variant="contained"
              onClick={() => handleSendWhatsapp("META")}
              sx={actionButtonSx}
            >
              Send WhatsApp
            </Button>
          )}
          {/* Action buttons that are moved to RightPanel when hideActionButtons is true */}
          {!hideActionButtons && (
            <>
              {initialLeadToRoughEstimate?.status !== "ONGOING" &&
                initialLeadToRoughEstimate?.status !== "POSTPONED" && (
                  <Button
                    disabled={loading || isLeadInactive}
                    variant="outlined"
                    sx={{
                      ...actionButtonSx,
                      borderRadius: 2,
                      backgroundColor: "error.main",
                      color: "white",
                      "&:hover": { backgroundColor: "error.dark" },
                    }}
                    onClick={() => {
                      if (onCancellation) {
                        onCancellation();
                      } else {
                        setOpenCancellation(true);
                      }
                    }}
                  >
                    Cancellation
                  </Button>
                )}
              {isMobile && values.is_payment_transaction_exist === 0 && (
                <Button
                  disabled={loading || isLeadInactive}
                  variant="contained"
                  onClick={handleAddAdvanceAmount}
                  sx={actionButtonSx}
                >
                  Add Advance Amount
                </Button>
              )}
              {hasSavedQuotation && (
                <Button
                  disabled={loading || isLeadInactive}
                  variant="contained"
                  onClick={() => setOpenPreview(quotationDocUuid)}
                  sx={actionButtonSx}
                >
                  Preview
                </Button>
              )}
              <Button
                disabled={loading || isLeadInactive}
                variant="contained"
                onClick={handleSaveAndPreview}
                sx={actionButtonSx}
              >
                {loading
                  ? "Saving..."
                  : hasSavedQuotation
                    ? isMobile ? "Update & Preview" : "Update & Preview Estimate"
                    : isMobile ? "Create & Preview" : "Create & Preview Estimate"}
              </Button>
              {values.is_payment_transaction_exist === 1 ? (
                <Box sx={{ flexShrink: 0 }}>
                  <ButtonWithRemark
                    disabled={loading || isLeadInactive}
                    confirmButtonText={hasSavedQuotation ? "Update" : "Create"}
                    onConfirm={(remark) => {
                      setFieldValue("remarks", remark);
                      handleSubmit();
                    }}
                    variant="contained"
                  />
                </Box>
              ) : (
                <RoleBasedCustomButton
                  disabled={isLeadInactive}
                  moduleId={ALLOWED_MODULES.EVENT_LEADS}
                  type="submit"
                  variant="contained"
                  sx={{
                    ...actionButtonSx,
                    px: isMobile ? 1 : 4,
                    borderRadius: 2,
                    bgcolor: "primary.main",
                    "&:hover": { bgcolor: "primary.dark" },
                  }}
                >
                  {hasSavedQuotation ? "Update" : "Create"}
                </RoleBasedCustomButton>
              )}
            </>
          )}
        </Box>
      </Stack>
    );
  };

  return (
    <PageLoader loading={leadLoading === LoadState.InProgress}>
      <PageContainer title={"Quotation"} description="this is inner page">
        <Box component={"form"} onSubmit={handleSubmit}>
          <Box
            sx={{
              borderRadius: 2.4,
              background: "#fff",
              overflow: "visible",
              minHeight: isMobile ? "auto" : "calc(100vh - 100px)",
              ...(isMobile && {
                boxShadow: "none",
                border: "none",
                padding: 0,
                background: "transparent",
              }),
            }}
          >
            {/* Header row: title + right actions */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
                p: 1.5,
              }}
            >
              <Typography
                variant="h2"
                fontSize={{
                  xs: "0.75rem",
                  sm: "0.9rem",
                  md: "1.1rem",
                  lg: "1.2rem",
                }}
                fontWeight={"700"}
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {isMobile ? "Quotation" : "Quotation Details"}
              </Typography>

              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <Tooltip
                  title={
                    !hasSavedQuotation
                      ? "Create a quotation first to preview it"
                      : ""
                  }
                  arrow
                >
                  <span>
                    <Button
                      disabled={loading || isLeadInactive || !hasSavedQuotation}
                      variant="contained"
                      size={"small"}
                      startIcon={<Visibility fontSize="small" />}
                      sx={{
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        fontSize: "0.8rem",
                        py: "4px",
                        px: 1.5,
                      }}
                      onClick={() => setOpenPreview(quotationDocUuid)}
                    >
                      Preview
                    </Button>
                  </span>
                </Tooltip>
                <ShareV2Button
                  leadUuid={quotationDocUuid}
                  disabled={loading || isLeadInactive || !hasSavedQuotation}
                />
              </Box>
            </Box>

            {/* Current quotation details */}
            {initialLeadToRoughEstimate && (
              <DetailInfoCard
                sx={{ mb: 1 }}
                columns={{ xs: 6, sm: 4, md: 3 }}
                fields={[
                  // Row 1: Guest Name | Contact Number | Email | Enquiry Date
                  {
                    label: "Guest Name",
                    icon: <PersonOutline sx={{ fontSize: 16 }} />,
                    value: initialLeadToRoughEstimate.name_of_guest || "--",
                  },
                  {
                    label: "Contact Number",
                    icon: <PhoneOutlined sx={{ fontSize: 16 }} />,
                    value: contactNumber || "--",
                  },
                  {
                    label: "Email",
                    icon: <EmailOutlined sx={{ fontSize: 16 }} />,
                    value: initialLeadToRoughEstimate.email || "--",
                  },
                  {
                    label: "Enquiry Date",
                    icon: <EventNoteOutlined sx={{ fontSize: 16 }} />,
                    value: initialLeadToRoughEstimate.date_of_visit
                      ? ftDate(initialLeadToRoughEstimate.date_of_visit)
                      : "--",
                  },
                  // Row 2: Handled By Employee | Source | Priority | Grand Total
                  {
                    label: "Handled By Employee",
                    icon: <SupportAgentOutlined sx={{ fontSize: 16 }} />,
                    value: initialLeadToRoughEstimate.handled_by_employee || "--",
                  },
                  {
                    label: "Source",
                    icon: <SourceOutlined sx={{ fontSize: 16 }} />,
                    value: initialLeadToRoughEstimate.source || "--",
                  },
                  {
                    label: "Priority",
                    icon: <FlagOutlined sx={{ fontSize: 16 }} />,
                    value: initialLeadToRoughEstimate.priority || "--",
                  },
                  {
                    label: "Total Guest",
                    icon: <PeopleRounded sx={{ fontSize: 16 }} />,
                    value: initialLeadToRoughEstimate.number_of_guests || "--",
                  },
                ]}
              />
            )}

            {/* Stay details are shared across all events of the quotation */}
            <QuotationStayDetails
              rows={stayDetails}
              rooms={rooms}
              total={stayDetailsTotal}
              isMobile={isMobile}
              readOnly={isInvoiceView}
              disabled={isLeadInactive}
              onAdd={handleAddStayRow}
              onRemove={handleRemoveStayRow}
              onChange={handleStayChange}
              getMissingFields={
                showStayErrors ? getStayRowMissingFields : undefined
              }
            />

            {/* Events — expand one to edit its item charges */}
            {isMobile ? renderEventCards() : renderEventsTable()}

            {/* Documents + totals */}
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} lg={6}>
                <CustomTextField
                  label="GST/PAN Number (Optional)"
                  name="gst_pan_number"
                  value={values.gst_pan_number || ""}
                  onChange={(e) => setFieldValue("gst_pan_number", e.target.value)}
                  fullWidth
                  size="small"
                  disabled={isLeadInactive}
                />
                <Box sx={{ mt: 2 }}>
                  <CustomFormLabel>Upload Documents</CustomFormLabel>
                  <FileUpload
                    multiple
                    variant="GALLERY"
                    galleryTileSize={96}
                    value={values.attachments || []}
                    onMultiChange={(data) => setFieldValue("attachments", data)}
                    onDelete={() => setFieldValue("attachments", null)}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={9} lg={6}>
                <QuotationSummary
                  subTotal={values.sub_total}
                  lineDiscountAmount={financialSummary.lineDiscountTotal}
                  discount={values.discount}
                  discountMode={values.discount_mode}
                  onDiscountChange={(v) => setFieldValue("discount", v)}
                  onDiscountModeChange={(mode) => setFieldValue("discount_mode", mode)}
                  overallDiscountAmount={financialSummary.overallDiscountAmount}
                  totalGst={Number(values.total_gst || 0)}
                  totalVat={Number(values.total_vat || 0)}
                  grandTotal={Number(values.grand_total || 0)}
                  totalPaid={Number(values.total_amount_paid || 0)}
                  remaining={
                    values.is_payment_transaction_exist === 1
                      ? Number(values.remaining_balance || 0)
                      : Number(values.grand_total || 0)
                  }
                />
              </Grid>
            </Grid>

            {renderActions()}
          </Box>
        </Box>
      </PageContainer>
      {openPreview && (
        <RoughEstimatePreviewV2
          open={true}
          uuid={openPreview}
          multiple_events={MULTIPLE_EVENTS_PREVIEW}
          onClose={() => setOpenPreview(null)}
        />
      )}
      {openAdvancePayment && (
        <PaymentDetailsDialog
          open={openAdvancePayment}
          leadUUID={leadUUID || ""}
          multipleEventsUUID={multipleEventsUuid}
          onClose={() => setOpenAdvancePayment(false)}
          isAdvance={isAdvance}
        />
      )}
      {openCancellation && (
        <PaymentDetailsDialog
          open={openCancellation}
          leadUUID={leadUUID || ""}
          multipleEventsUUID={multipleEventsUuid}
          onClose={() => setOpenCancellation(false)}
          isAdvance={isAdvance}
          isCancellation={isCancellation}
        />
      )}
      {errorDialog && (
        <Dialog
          open={!!errorDialog}
          title="Quotation Incomplete"
          subtitle="Required information is missing"
          variant="error"
          headerIcon={<ErrorOutline />}
          size="sm"
          onClose={() => setErrorDialog(null)}
          actions={[
            {
              type: "button",
              variant: "contained",
              label: "OK",
              onClick: () => setErrorDialog(null),
            },
          ]}
        >
          <Stack
            direction="row"
            alignItems="flex-start"
            spacing={1.25}
            py={1}
            sx={{
              color: "error.main",
              bgcolor: (t) => `${t.palette.error.light}1A`,
              border: (t) => `1px solid ${t.palette.error.light}`,
              borderRadius: 2,
              px: 2,
            }}
          >
            <ErrorOutline sx={{ color: "error.main", mt: "2px" }} />
            <Typography
              variant="h5"
              fontWeight={500}
              sx={{ color: "error.main" }}
            >
              {errorDialog}
            </Typography>
          </Stack>
        </Dialog>
      )}
      {createLeadSourceEvents && (
        <CreateEventLeadsDialog
          open={true}
          baseLead={initialLeadToRoughEstimate}
          sourceEvents={createLeadSourceEvents}
          onClose={() => setCreateLeadSourceEvents(null)}
          onSuccess={(createdLeads) => {
            setCreateLeadSourceEvents(null);
            // Parent reloads the lead's events (the rows), then refresh the quotation itself
            onEventLeadsCreated?.(createdLeads);
            fetchQuotation();
          }}
        />
      )}
    </PageLoader>
  );
};
